// deps
import { useCallback, useContext, useMemo } from "react";

// internal-deps
import { RenderingDetails, SavingsLineItemData, SavingsRightSectionType } from "@swiggy-private/cart-types";
import { isEmpty } from "@swiggy-private/js-utils";

// constants
import { APPLY_COUPON_TEXTS } from "@cart-client/constants/Coupon";
import { CART_V2_APPLY_COUPON_ICON } from "@cart-client/constants/index";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
import { AnalyticsContext } from "@swiggy-private/checkout-core";

// helpers
import { getSavingsCornerClickStreamData } from "@cart-client/helpers/SavingsCorner";
import { impressionSavingsCornerLineItem } from "@cart-client/analytics/events/SavingsCorner";
import { getCartId } from "@cart-client/helpers/Cart";

//types and interfaces
export interface UseDeliveryFeeAndExtraSavingsReturnType {
    deliveryFeeAndExtraSavingsLineItems: SavingsLineItemData[];
}
export interface IProps {
    deliveryFeeAndExtraSavingsRenderingDetails: RenderingDetails[];
}

export const useDeliveryFeeAndExtraSavings = ({
    deliveryFeeAndExtraSavingsRenderingDetails,
}: IProps): UseDeliveryFeeAndExtraSavingsReturnType => {
    const {
        cartState: { reviewedCart: cartResponse },
    } = useContext(CartContext);
    const { impressionEvent } = useContext(AnalyticsContext);

    const cartId = !isEmpty(cartResponse) ? getCartId(cartResponse) : "";

    const onSavingsLineItemLoad = useCallback(
        (title?: string, subtitle?: string): void => {
            const lineItemData = getSavingsCornerClickStreamData(title, subtitle);

            impressionSavingsCornerLineItem(impressionEvent, cartId, lineItemData);
        },
        [cartId, impressionEvent],
    );

    const deliveryFeeAndExtraSavingsLineItems = useMemo((): SavingsLineItemData[] => {
        if (isEmpty(deliveryFeeAndExtraSavingsRenderingDetails)) {
            return [];
        }

        return deliveryFeeAndExtraSavingsRenderingDetails.map(renderingDetails => {
            const title: SavingsLineItemData["title"] = {
                value: renderingDetails?.meta?.title || "",
            };

            const rightSection = {
                type: SavingsRightSectionType.LABEL,
                value: APPLY_COUPON_TEXTS.APPLIED,
            };

            return {
                icon: renderingDetails?.icon || CART_V2_APPLY_COUPON_ICON,
                title,
                rightSection,
                onLoad: () => {
                    onSavingsLineItemLoad(title.value);
                },
            };
        });
    }, [deliveryFeeAndExtraSavingsRenderingDetails, onSavingsLineItemLoad]);

    return { deliveryFeeAndExtraSavingsLineItems };
};
